import { Actions } from "@Context/common/general/context";
import { AnalyticsEvents, DeviceFamilies, DeviceModels } from "@Enums";
import stringToAction from "@Helpers/string-to-action";
import * as Sentry from "@sentry/react";
import { createMockPlaySessionController, MOCKABLE_PLAY_SESSION_EVENTS } from "./mock-play-session-controller";

// Delay time for bridge to be initialized via Vuplex before web is initialized
const webInitDelay = 1000;
class UnityBridge {
  OutgoingWebEvents = {
    /* Bridge Ops */
    InvokeCallback: "InvokeCallback",
    WebReady: "WebReady",
    BridgeTest: "BridgeTest",
    /* System Ops */
    TextFieldFocused: "TextFieldFocused",
    TextFieldBlurred: "TextFieldBlurred",
    ExitApplicationSignal: "ExitApplicationSignal",
    NavigateToMenuSignal: "NavigateToMenuSignal",
    PlatformServiceRequest: "PlatformServiceRequest",
    OpenExternalUrl: "OpenExternalUrl",
    OverrideGameUrl: "OverrideGameUrl",
    ShowWebUI: "ShowWebUI",
    HideWebUI: "HideWebUI",
    PresentationMode: "PresentationMode",
    TriggerHaptics: "TriggerHaptics",
    /* General Ops */
    StartPlaylist: MOCKABLE_PLAY_SESSION_EVENTS.StartPlaylist,
    ReplayEvent: "ReplayEvent",
    ReplayUiEvent: "ReplayUiEvent",
    BybCatEvent: "BybCatEvent",
    PlaySound: "PlaySound",
    SetPreferences: "SetPreferences",
    AccountInfoUpdated: "AccountInfoUpdated",
    NotificationEvent: "NotificationEvent",
    GFootball: MOCKABLE_PLAY_SESSION_EVENTS.GFootball,
    /* Editor Ops */
    EditorLoadScenario: "EditorLoadScenario",
    EditorScenarioDataRequested: "EditorScenarioDataRequested",
    EditorValidateScenario: "EditorValidateScenario",
    StartValidation: "StartValidation",
    FinishValidation: "FinishValidation",
    LoadEditor: "LoadEditor",
    UnloadEditor: "UnloadEditor",
    PlayTest: "PlayTest",
  };

  IncomingWebEvents = {
    InvokeCallback: "InvokeCallback",
    GameReady: "GameReady",
    BridgeTest: "BridgeTest",
    /* Results */
    SubmitMetrics: "SubmitMetrics",
    UpdateResults: "UpdateResults",
    /* Game */
    GameplayEntered: "GameplayEntered",
    GameplayExited: "GameplayExited",
    RequestScenario: "RequestScenario",
    /* Editor */
    EditorEntered: "EditorEntered",
    EditorExited: "EditorExited",
    EditorLoaded: "EditorLoaded",
    EditorUnloaded: "EditorUnloaded",
    EditorSaveAsNewScenario: "EditorSaveAsNewScenario",
    EditorSaveScenarioChanges: "EditorSaveScenarioChanges",
    /* Analytics */
    AnalyticsEvent: "AnalyticsEvent",
    /* System */
    AppFocusLost: "AppFocusLost",
    AppFocusRestored: "AppFocusRestored",
    RequestSubscriptions: "RequestSubscriptions",
    WebSocketServerReady: "WebSocketServerReady",
    ReplayDuration: "ReplayDuration",
    /* Replay */
    ReplayEvent: "ReplayEvent",
    ReplayUiEvent: "ReplayUiEvent",
    ReplayFrame: "ReplayFrame",
    ScenarioRetryReplay: "ScenarioRetryReplay",
    EnterReplayMode: "EnterReplayMode",
    ExitReplayMode: "ExitReplayMode",
    ReplayPassOptions: "ReplayPassOptions",
    /* BYBCAT */
    BybCatEvent: "BybCatEvent",
    /* PlaySession */
    PlaySessionEvent: "PlaySessionEvent",
    /* NotificationEvent */
    NotificationEvent: "NotificationEvent",
  };

  _mockPlaySessionController = null;

  constuctor() {
    if (window.unityBridge) return;
    this._state = null;
    this._dispatch = null;
    this._callbacks = null;
    this._callbackIDCounter = null;
  }

  addCallback({ callbackID, callback }) {
    if (this._callbacks[callbackID]) return console.error("Callback ID: ", callbackID, " already exists! This is a key conflict");
    this._callbacks[callbackID] = callback;
  }

  removeCallback({ callbackID }) {
    if (!this._callbacks[callbackID]) return console.error("Callback ID:", callbackID, " does not exist or has already been removed");
    delete this._callbacks[callbackID];
  }

  invokeCallback({ callbackID, payload }) {
    if (!this._callbacks[callbackID]) return console.error("Callback ID:", callbackID, " does not exist or has already been removed");
    const callback = this._callbacks[callbackID];
    callback(payload);
    this.removeCallback({ callbackID });
  }

  initBridge({ state, dispatch }) {
    if (window.unityBridge) return;
    this._state = state;
    this._dispatch = dispatch;
    this._callbackIDCounter = 0;
    this._callbacks = {};
    window.unityBridge = true;

    if (process.config.MOCK_PLAY_SESSIONS) {
      this._mockPlaySessionController = createMockPlaySessionController({
        dispatch: this._dispatch,
        updateResultsAction: Actions.SessionActions.UpdateResults,
        emitComplete: event => this._state.app.events.playSessionEvent.emit(event),
        emitGameplayEntered: event => this.emitGameplayEntered(event),
        emitGameplayExited: event => this.emitGameplayExited(event),
        reportError: (error, context) => {
          console.error(`Mock play session ${context.playSessionId} failed while updating cognitive result ${context.cognitiveResultId}`, error);
          Sentry.captureException(error, {
            tags: { type: "mock-play-session" },
            extra: context,
          });
        },
      });
    }

    // Initialization Timeout - We wait for vuplex to exist otherwise we fire a bridge init timeout for browser apps to continue
    const bridgeInitTimeout = setTimeout(() => {
      this._state.app.events.bridgeInitialized.emit();
      this._dispatch({
        type: Actions.CoreActions.UpdateState,
        payload: {
          app: { bridgeInitialized: true },
        },
      });
    }, 1000);

    if (process.config.MOCK_BRIDGE || process.config.MOCK_VR || process.config.MOCK_MOBILE) {
      clearTimeout(bridgeInitTimeout);
      if (process.config.UNITY_LOGS) {
        console.log("#Mocked UnityBridge Initialized");
      }
      this._state.app.events.bridgeInitialized.emit();
      this._dispatch({
        type: Actions.CoreActions.UpdateState,
        payload: {
          device: {
            isUnityEmbedded: true,
          },
          app: { bridgeInitialized: true },
        },
      });
    }

    if (window.vuplex) {
      clearTimeout(bridgeInitTimeout);
      this.addMessageListener();
      this.sendReadyEvent();
    } else {
      window.addEventListener("vuplexready", () => {
        clearTimeout(bridgeInitTimeout);
        this.addMessageListener();
        this.sendReadyEvent();
      });
    }

    // Setup binding for updating Unity with user account info
    this._state.auth.events.accountInfoUpdated.on(data => {
      this.dispatchWebEvent({ type: this.OutgoingWebEvents.AccountInfoUpdated, payload: data });
    });
  }

  updateState(state) {
    this._state = state;
  }

  addMessageListener() {
    window.vuplex.addEventListener("message", this.onUnityEventReceived.bind(this));
  }

  emitGameplayEntered(payload) {
    this._state.app.events.gameplayEntered.emit(payload);
    this._state.app.events.passThroughStateChanged.emit();
  }

  emitGameplayExited(payload) {
    this._state.app.events.gameplayExited.emit(payload);
    this._state.app.events.passThroughStateChanged.emit();
  }

  onUnityEventReceived(event) {
    if (!event || !event.data) return console.log("Empty event received");

    try {
      var eventData = JSON.parse(event.data);
    } catch {
      return console.error("Failed JSON parse on event:", event);
    }

    Sentry.addBreadcrumb({
      category: "UnityBridge",
      type: eventData.type,
      message: `<- Incoming unity event received - ${eventData?.type || "unknown"}`,
      data: eventData,
    });

    if (process.config.UNITY_LOGS) {
      console.log("#UnityBridge <--- Incoming event received: ", JSON.stringify(eventData), eventData);
    }

    switch (eventData.type) {
      case this.IncomingWebEvents.InvokeCallback:
        const invokeCallbackID = eventData.invoke_callback_id;
        if (!invokeCallbackID) return console.error("invoke_callback_id in event: ", eventData, " does not exist");
        this.invokeCallback({
          callbackID: invokeCallbackID,
          payload: eventData.payload,
        });
        break;
      case this.IncomingWebEvents.BridgeTest:
        this._dispatch({
          type: Actions.CoreActions.UpdateState,
          payload: {
            device: {
              isUnityEmbedded: true,
            },
          },
        });
        this.dispatchWebEvent({
          type: this.OutgoingWebEvents.InvokeCallback,
          invokeCallbackID: eventData.success_id,
          payload: eventData.payload,
        });
        break;
      case this.IncomingWebEvents.GameReady:
        this.sendBridgeTestEvent();

        // Check payload exists due to duplicate gameready event
        if (eventData.payload) {
          this._state.app.events.gameReadyEvent.emit(eventData.payload);
          this._state.app.events.bridgeInitialized.emit(eventData.payload);
          this._dispatch({
            type: Actions.CoreActions.UpdateState,
            payload: {
              app: {
                bridgeInitialized: true,
                clientVersion: eventData.payload.client_version,
                applicationIdentifier: eventData.payload.application_identifier || null,
              },
              device: {
                family: eventData.payload.device_family || DeviceFamilies.Unknown,
                model: eventData.payload.device_model || DeviceModels.Unknown,
                isVR: eventData.payload.is_vr || process.config.MOCK_VR,
                isMobile: eventData.payload.is_mobile || process.config.MOCK_MOBILE,
                isUnityEmbedded: true,
              },
            },
          });
        }
        break;
      case this.IncomingWebEvents.GameplayEntered:
        this.emitGameplayEntered(eventData.payload);
        break;
      case this.IncomingWebEvents.GameplayExited:
        this.emitGameplayExited(eventData.payload);
        break;
      case this.IncomingWebEvents.EditorEntered:
        this._state.editor.events.editorEntered.emit();
        this._dispatch({
          type: Actions.CoreActions.UpdateState,
          payload: {
            app: { passThrough: true },
          },
        });
        this._state.app.events.passThroughStateChanged.emit();
        break;
      case this.IncomingWebEvents.EditorExited:
        this._state.editor.events.editorExited.emit();
        this._dispatch({
          type: Actions.CoreActions.UpdateState,
          payload: {
            app: { passThrough: false },
          },
        });
        this._state.app.events.passThroughStateChanged.emit();
        break;
      case this.IncomingWebEvents.EditorSaveAsNewScenario:
        this._state.editor.events.editorSaveAsNewScenario.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.EditorSaveScenarioChanges:
        this._state.editor.events.editorSaveScenarioChanges.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.AppFocusLost:
        this._state.app.events.appFocusLost.emit();
        break;
      case this.IncomingWebEvents.AppFocusRestored:
        this._state.app.events.appFocusRestored.emit(eventData.payload.unfocused_duration);
        break;
      case this.IncomingWebEvents.BybCatEvent:
        this._state.app.events.bybCatEvent.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.NotificationEvent:
        this._state.app.events.notificationEvent.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.RequestScenario:
        this.dispatchAction(Actions.ScenariosActions.RequestScenario, eventData);
        break;
      case this.IncomingWebEvents.ReplayEvent:
        eventData.payload.user_id = this._state.user.id;

        switch (eventData.payload.action) {
          case "create":
            this.dispatchAction(Actions.ReplayActions.CreateReplay, eventData);
            break;
          case "request":
            this.dispatchAction(Actions.ReplayActions.RequestReplay, eventData, payload =>
              payload?.download_url ? { download_url: payload.download_url } : payload
            );
            break;
        }

        this._state.app.events.replayEvent.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.ScenarioRetryReplay:
        this._state.app.events.scenarioRetryReplay.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.PlaySessionEvent:
        this._state.app.events.playSessionEvent.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.RequestSubscriptions:
        this.dispatchAction(Actions.SubscriptionsActions.GetUserSubscriptions, eventData);
        break;
      case this.IncomingWebEvents.ReplayDuration:
        this._state.app.events.replayDuration.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.EnterReplayMode:
        this._state.app.events.enterReplayMode.emit();
        break;
      case this.IncomingWebEvents.ExitReplayMode:
        this._state.app.events.exitReplayMode.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.ReplayFrame:
        this._state.app.events.replayFrame.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.ReplayPassOptions:
        this._state.app.events.replayPassOptions.emit(eventData.payload);
        break;
      case this.IncomingWebEvents.ReplayUiEvent:
        this._state.app.events.replayUiEvent.emit(eventData.payload);
        break;
      default:
        this.dispatchAction(stringToAction(eventData.type), eventData);
        break;
    }
  }

  dispatchAction(actionType, eventData, mapSuccessPayload = payload => payload) {
    let actionConfig = {
      type: actionType,
    };
    if (eventData.payload) actionConfig["payload"] = eventData.payload;
    if (eventData.success_id)
      actionConfig["onSuccess"] = payload => {
        this.dispatchWebEvent({
          type: this.OutgoingWebEvents.InvokeCallback,
          invokeCallbackID: eventData.success_id,
          payload: mapSuccessPayload(payload),
        });
      };
    if (eventData.error_id)
      actionConfig["onError"] = payload => {
        this.dispatchWebEvent({
          type: this.OutgoingWebEvents.InvokeCallback,
          invokeCallbackID: eventData.error_id,
          payload: payload,
        });
      };
    this._dispatch(actionConfig);
  }

  sendReadyEvent() {
    this.dispatchWebEvent({
      type: this.OutgoingWebEvents.WebReady,
    });
  }

  sendBridgeTestEvent() {
    this.dispatchWebEvent({
      type: this.OutgoingWebEvents.BridgeTest,
      payload: { testing: "payload_from_web" },
      successCallback: payload => {
        if (process.config.UNITY_LOGS) {
          console.log("#UnityBridge - Bridge Test Successful", payload);
        }
      },
    });
  }

  dispatchWebEvent({
    type,
    payload = {},
    invokeCallbackID = null,
    successCallback = null,
    errorCallback = null,
    isLog = false,
    enableLoader = false,
    //cancel
  }) {
    let webEvent = { sender: "web_client", type };

    let promiseResolve;
    const loadPromise = new Promise(resolve => {
      promiseResolve = resolve;
    });

    if (this._mockPlaySessionController?.handle({ type, payload })) return;

    if (type === this.OutgoingWebEvents.ReplayEvent && payload.action === "start") {
      this._dispatch({
        type: Actions.AppActions.AnalyticsEvent,
        payload: {
          type: AnalyticsEvents.mobile_app_replay_started,
        },
      });
    }

    if ((enableLoader && successCallback) || errorCallback) {
      this._state.loading.events.enableLoader.emit(loadPromise);
    }

    if (payload) webEvent["payload"] = payload;
    if (type === this.OutgoingWebEvents.InvokeCallback) {
      // Sent only when instructing unity client to invoke a callback matching the ID on receiving of this event
      if (invokeCallbackID) webEvent["invoke_callback_id"] = invokeCallbackID;
    } else {
      // Sent only when instructing unity client to return an invoke callback event matching the ID after completing this events action
      if (successCallback) {
        this._callbackIDCounter += 1;
        const successCallbackID = this._callbackIDCounter;
        this.addCallback({
          callbackID: successCallbackID,
          callback: payload => {
            promiseResolve();
            successCallback(payload);
          },
        });
        webEvent["success_id"] = successCallbackID;
      }
      if (errorCallback) {
        this._callbackIDCounter += 1;
        const errorCallbackID = this._callbackIDCounter;
        this.addCallback({
          callbackID: errorCallbackID,
          callback: payload => {
            promiseResolve();
            errorCallback(payload);
          },
        });
        webEvent["error_id"] = errorCallbackID;
      }
    }
    if (window.vuplex && typeof window.vuplex.postMessage === "function") {
      window.vuplex.postMessage(webEvent);
      if (process.config.UNITY_LOGS) {
        console.log("#UnityBridge ---> Outgoing event dispatched:", JSON.stringify(webEvent), webEvent);
      }
      Sentry.addBreadcrumb({
        category: "UnityBridge",
        type: type,
        message: `-> Outgoing unity event - ${type || "unknown"}`,
        data: payload,
      });
    }
  }
}

const instance = new UnityBridge();
export default instance;
